import { useCallback, useEffect, useState } from "react";
import usePolling from "./usePolling";
import { aiSummaryOf, fetchAISession, fetchAISessionsByInterview, shouldPollAISession } from "../utils/aiInterviewApi";

const POLL_MS = 15000;

// AI sessions for a list of interviews: interview id → AISession.
// `interviews` is [{ id, startTime, source }] where `source` is the interview (for its ai_interview summary).
// Interviews whose AI isn't final and that start within ±1 hour are refreshed every 15 s unless `paused`.
// With `autoLoad` off the caller loads the list with `reload()` (e.g. after loading its interviews).
const useAISessions = ({ autoLoad = true, filters, interviews = [], paused = false }) => {
  const [sessions, setSessions] = useState({});
  const filtersKey = JSON.stringify(filters || {});

  const reload = useCallback(async () => {
    try {
      setSessions(await fetchAISessionsByInterview(JSON.parse(filtersKey)));
    } catch (err) {
      // The list still works from the ai_interview summary on each interview
      console.error("Error fetching AI interview sessions:", err);
    }
  }, [filtersKey]);

  useEffect(() => {
    if (autoLoad) reload();
  }, [autoLoad, reload]);

  const update = useCallback((interviewId, session) => {
    setSessions((previous) => {
      const next = { ...previous };
      if (session) next[interviewId] = session;
      else delete next[interviewId];
      return next;
    });
  }, []);

  const liveIds = interviews
    .filter((item) => {
      const summary = aiSummaryOf(item.source, sessions[item.id]);
      return item.id && summary?.enabled && shouldPollAISession(summary.status, item.startTime);
    })
    .map((item) => item.id);

  usePolling(
    () =>
      Promise.all(
        liveIds.map((id) =>
          fetchAISession(id)
            .then((session) => update(id, session))
            .catch((err) => console.error("Error polling AI interview:", err))
        )
      ),
    POLL_MS,
    !paused && liveIds.length > 0
  );

  return { sessions, reload, update };
};

export default useAISessions;
